import { APP_VERSION, CLIENT_HOMEPAGE } from 'config';
import { useAuth } from 'modules/auth/providers/AuthProvider';
import { useNavBar } from 'modules/nav/provider/NavBarProvider';
import SalesRepNavBar from 'modules/salesrep/OfferingDetails/components/SalesRepNavBar';
import { useClient } from 'modules/settings/providers/ClientProvider';
import Image from 'next/legacy/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useState } from 'react';
import { AdvisorViewIcon } from 'ui/AdvisorViewIcon';
import Container from 'ui/Container';
import Divider from 'ui/Divider';
import * as AllIcon from 'ui/Icons';
import { Close } from 'ui/Icons';
import Loader from 'ui/Loader';
import LogoNavbar from 'ui/Logos/LogoNavbar';
import NavSideBar from 'ui/NavSideBar';
import Notification from 'ui/Notification';
import Select from 'ui/Select';
import theme from 'ui/theme/config';
import RightSideBar from 'ui/theme/Header/RightSideBar';
import { LayoutInterface, VariantTypes } from 'ui/uiTypes';
import UserAvatar from 'ui/UserAvatar';
import { ComponentShield } from 'utils/guards/shield/components/ComponentShield';
import { isMaxWidth, useDetectMobileDevice } from 'utils/hooks/useWindow';

import NavbarItem from '../NavbarItem';
import {
  AdminLogoWrapper,
  AppVersionAndCopyrightWrapper,
  AppVersionWrapper,
  ClientsWrapper,
  CopyrightWrapper,
  LoaderWrapper,
  LogoWrapper,
} from './styles';
import {
  ActiveAdvisorName,
  ActiveAdvisorWrapper,
  AdminAvatarContainer,
  AdvisorViewIconWrapper,
  AdvisorWrapper,
  AppVersion,
  Identifications,
  NavbarContainer,
  WelcomeMessage,
  Wrapper,
} from './styles';

interface LinksData {
  label: string;
  pathname: string;
  divider?: boolean;
}

interface Props {
  showLinks?: boolean;
  showSidebars?: boolean;
  dataLinksAuth?: LinksData[];
  isAdvisorView?: boolean;
  isAdminPortalPage?: boolean;
  isSalesrepShadow?: boolean;
  handleAdvisorViewOpen?: () => void;
  handleNavigationClick?: (link: string) => void;
}

const Navbar: React.FC<Props & LayoutInterface> = ({
  variant,
  showLinks,
  isAdmin,
  isAdminPortalPage,
  isSalesRep,
  adminIsAuthenticated,
  showSidebars = true,
  leftLoggedingMenu,
  rightNonLoggMenu,
  title,
  isAdvisorView,
  isSalesrepShadow,
  handleAdvisorViewOpen,
  handleNavigationClick,
}) => {
  const [showRightSideBar, setShowRightSideBar] = useState<boolean>(false);
  const closeRight = () => setShowRightSideBar(false);
  const openRight = () => setShowRightSideBar(true);

  const isSecondary = variant == VariantTypes.SECONDARY && !isAdmin;
  const isMobile = isMaxWidth('sm');
  const { isMobileDevice } = useDetectMobileDevice();
  const { state, logout, setActiveInvestor, activeInvestor } = useAuth();
  const { closeLeftNavBar, showLeftNavBar, leftOpen } = useNavBar();
  const router = useRouter();
  const {
    clients,
    client,
    isLoading: isClientLoading,
    errorMessage: clientErrorMessage,
    isClientSwitchHidden,
    handleClientChange,
    handleErrorReset: handleClientErrorReset,
  } = useClient();

  const callLogout = () => {
    logout();
    closeLeftNavBar();
  };

  const handleLogoClick = () => {
    const link = CLIENT_HOMEPAGE;
    if (handleNavigationClick) {
      handleNavigationClick(link);
    } else {
      router.push(link);
    }
  };

  const formatNames = (firstName?: string, lastName?: string) => {
    if (firstName && lastName) return `${firstName} ${lastName}`;
    return firstName ? firstName : lastName ? firstName : '';
  };

  const user = {
    firstName: state.userJob?.contact?.firstName as string | undefined,
    lastName: state.userJob?.contact?.lastName as string | undefined,
    image: undefined,
  };

  if (isAdminPortalPage || isAdmin) {
    return (
      <Wrapper
        as={NavbarContainer}
        centerLogo={!showSidebars}
        data-testid="nav-wrapper-id"
      >
        <NavSideBar
          active={leftOpen}
          side="left"
          onClickClose={closeLeftNavBar}
        >
          {state.loggedIn && (
            <Identifications>
              <UserAvatar user={user} />
            </Identifications>
          )}
          <ComponentShield RBAC showForRole="ADMIN" userRole={state.user?.role}>
            <NavbarItem
              label="Offerings"
              href="/current-offerings"
              handleClick={closeLeftNavBar}
            />
          </ComponentShield>
          {state.loggedIn ? (
            <>
              <NavbarItem
                label="Investment Summary"
                href="/dashboard"
                handleClick={closeLeftNavBar}
              />
              <NavbarItem
                label="Documents"
                href="/documents"
                handleClick={closeLeftNavBar}
              />
              <NavbarItem
                label="Tax Center"
                href="/tax-center"
                handleClick={closeLeftNavBar}
              />
              <NavbarItem
                label="Settings"
                href="/settings"
                handleClick={closeLeftNavBar}
              />
              <NavbarItem
                label="Sign Out"
                href="/signout"
                withoutLink
                handleClick={callLogout}
              />
            </>
          ) : (
            <NavbarItem
              label="Sign In"
              href="/auth/signin"
              handleClick={closeLeftNavBar}
            />
          )}
        </NavSideBar>
        <LogoWrapper data-testid="LogoNavbar" onClick={handleLogoClick}>
          {!isAdminPortalPage && !isSecondary ? (
            <LogoNavbar
              width={isMobile ? 160 : 228}
              height={36}
              variant={variant}
              strokeWidth={1}
            />
          ) : (
            <LogoNavbar
              width={164}
              height={37}
              variant={variant}
              strokeWidth={1}
            />
          )}
        </LogoWrapper>
        {isSecondary ? (
          <RightSideBar
            {...{
              isSecondary,
              leftLoggedingMenu,
              showRightSideBar,
              variant,
              rightNonLoggMenu,
              showSidebars,
              showLinks,
              openRight,
              closeRight,
              isMobile,
            }}
            loggedIn={state.loggedIn}
          />
        ) : (
          <span></span>
        )}
        {adminIsAuthenticated && (
          <AdminAvatarContainer>
            <WelcomeMessage>
              {formatNames(user?.firstName, user?.lastName)}
            </WelcomeMessage>
            <Image
              src="/assets/placeholderAdmin.png"
              layout="fixed"
              width={40}
              height={40}
            />
          </AdminAvatarContainer>
        )}
      </Wrapper>
    );
  }
  if (isSalesRep) {
    if (isMobileDevice) {
      return (
        <Wrapper mobileNavPadding={true} as={NavbarContainer}>
          <SalesRepNavBar title={title} isBoxShadow={isSalesrepShadow} />
        </Wrapper>
      );
    }
    return (
      <Wrapper as={NavbarContainer}>
        <Link passHref href={CLIENT_HOMEPAGE} data-testid="LogoNavbar">
          <LogoNavbar
            width={164}
            height={37}
            variant={variant}
            strokeWidth={1}
          />
        </Link>
      </Wrapper>
    );
  }
  return (
    <Wrapper
      as={
        isSecondary && !isAdmin
          ? Container
          : isAdmin
          ? NavbarContainer
          : undefined
      }
      centerLogo={!showSidebars}
    >
      {showSidebars && !isSecondary && !isAdmin && (
        <AllIcon.MenuIcon
          color={theme.colors.black[900]}
          height={30}
          width={30}
          onClick={showLeftNavBar}
        />
      )}
      <NavSideBar active={leftOpen} side="left" onClickClose={closeLeftNavBar}>
        {state.loggedIn && (
          <Identifications>
            <UserAvatar user={user} />
          </Identifications>
        )}
        {!isClientSwitchHidden && state.loggedIn && clients.length > 1 && (
          <>
            <ClientsWrapper>
              <Select
                options={clients}
                value={clients.find((option) => option.value === client)}
                noDefaultColor
                onChange={(option) =>
                  handleClientChange(option?.value || 'relevant', callLogout)
                }
                size="small"
              />
              {isClientLoading && (
                <LoaderWrapper>
                  <Loader toColor={theme.colors.primary.main} />
                </LoaderWrapper>
              )}
              {clientErrorMessage && (
                <Notification
                  state="danger"
                  msg={clientErrorMessage}
                  onClick={handleClientErrorReset}
                />
              )}
            </ClientsWrapper>
          </>
        )}

        <Divider marginTop={16} marginBottom={0} />

        {client === 'relevant' ? (
          <ComponentShield RBAC showForRole="ADMIN" userRole={state.user?.role}>
            <NavbarItem
              label="Offerings"
              href="/current-offerings"
              handleClick={closeLeftNavBar}
            />
          </ComponentShield>
        ) : null}
        {state.loggedIn ? (
          <>
            <NavbarItem
              label="Investment Summary"
              href="/dashboard"
              handleClick={closeLeftNavBar}
            />
            <NavbarItem
              label="Documents"
              href="/documents"
              handleClick={closeLeftNavBar}
            />
            <NavbarItem
              label="Tax Center"
              href="/tax-center"
              handleClick={closeLeftNavBar}
            />
            <NavbarItem
              label="Settings"
              href="/settings"
              handleClick={closeLeftNavBar}
            />
            <NavbarItem
              label="Sign Out"
              href="/signout"
              withoutLink
              handleClick={callLogout}
            />
          </>
        ) : (
          <NavbarItem
            label="Sign In"
            href="/auth/signin"
            handleClick={closeLeftNavBar}
          />
        )}

        <AppVersionAndCopyrightWrapper>
          <AppVersionWrapper>
            <Image
              src="/assets/relevant-app-logo.png"
              layout="fixed"
              width={32}
              height={32}
            />
            {APP_VERSION.version && (
              <AppVersion>v. {APP_VERSION.version}</AppVersion>
            )}
          </AppVersionWrapper>
          <CopyrightWrapper>
            <p>
              © {`${new Date().getFullYear()} `}
              <a href="https://relevant.us" target="_blank">
                Relevant Equity Systems Inc.
              </a>
            </p>
            <p>All rights reserved</p>
          </CopyrightWrapper>
        </AppVersionAndCopyrightWrapper>
      </NavSideBar>
      <Link
        passHref
        href={CLIENT_HOMEPAGE}
        data-testid="LogoNavbar"
        style={{ margin: 'auto' }}
      >
        {!isSecondary ? (
          <AdminLogoWrapper
            isAdvisorView={isAdvisorView && !isMobile}
            currentInvestorLength={
              (activeInvestor?.investor
                ? activeInvestor?.investor?.length + 5
                : 12) *
                8 +
              55
            }
          >
            <LogoNavbar
              width={isMobile ? 160 : 228}
              height={36}
              variant={variant}
              strokeWidth={1}
            />
          </AdminLogoWrapper>
        ) : (
          <LogoNavbar
            width={164}
            height={37}
            variant={VariantTypes.SECONDARY}
            strokeWidth={1}
          />
        )}
      </Link>
      {isSecondary ? (
        <RightSideBar
          {...{
            isSecondary,
            leftLoggedingMenu,
            showRightSideBar,
            variant,
            rightNonLoggMenu,
            showSidebars,
            showLinks,
            openRight,
            closeRight,
            isMobile,
          }}
          loggedIn={state.loggedIn}
        />
      ) : (
        <span></span>
      )}
      {isAdmin && adminIsAuthenticated && (
        <AdminAvatarContainer>
          <WelcomeMessage>
            {formatNames(user?.firstName, user?.lastName)}
          </WelcomeMessage>
          <Image
            src="/assets/placeholderAdmin.png"
            layout="fixed"
            width={40}
            height={40}
          />
        </AdminAvatarContainer>
      )}
      {isAdvisorView && (
        <AdvisorWrapper>
          {activeInvestor?.investor ? (
            <ActiveAdvisorWrapper>
              <ActiveAdvisorName>{activeInvestor?.investor}</ActiveAdvisorName>
              <Close
                state="advisorView"
                onClick={() => setActiveInvestor(undefined)}
                width="10"
                height="10"
              />
            </ActiveAdvisorWrapper>
          ) : (
            <ActiveAdvisorWrapper>
              <ActiveAdvisorName>All Clients</ActiveAdvisorName>
            </ActiveAdvisorWrapper>
          )}
          <AdvisorViewIconWrapper onClick={handleAdvisorViewOpen}>
            <AdvisorViewIcon />
          </AdvisorViewIconWrapper>
        </AdvisorWrapper>
      )}
    </Wrapper>
  );
};

export default Navbar;
